import { CommonModule } from '@angular/common';
import {
    Component,
    ElementRef,
    Input,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import {
    FormBuilder,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SelectGeneralApplicationComponent } from '../select-general-application/select-general-application.component';
import { InputGeneralApplicationComponent } from '../input-general-application/input-general-application.component';
import { InputSelectGeneralComponent } from 'app/components/input-select-general/input-select-general.component';
import { ActivatedRoute, Router } from '@angular/router';
import { ApplicationService } from 'app/services/application.service';
import {
    parseJSON,
    isEmpty,
    scrollToFirstInvalidControl,
} from '../../../../shared/utils';
import { MatRadioModule } from '@angular/material/radio';
import { RevampSelectGeneralApplicationComponent } from '../revamp-select-general-application/revamp-select-general-application.component';
import { RevampInputGeneralApplicationComponent } from '../revamp-input-general-application/revamp-input-general-application.component';
import { MatDividerModule } from '@angular/material/divider';
import { ApplicationDashboardService } from 'app/application/services/application-dashboard.service';
import { lastValueFrom, map } from 'rxjs';
import { Subject, takeUntil } from 'rxjs';
import { NoopScrollStrategy } from '@angular/cdk/overlay';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmInfoComponent } from '../business-info/confirm-info/confirm-info.component';
import {
    monthYearLabel,
    tenureOptionsMonths,
    tenureOptionsYears,
} from './financial-info-dropdown-options';
import { ToastrService } from 'ngx-toastr';
import { DashboardService } from 'app/modules/dashboard/dashboard.service';
import { LoaderComponent } from 'app/components/loader/loader.component';
import { IdaMessageBridgeService } from 'app/services/ida-message-bridge.service';

@Component({
    selector: 'financial-info',
    templateUrl: './financial-info.component.html',
    styleUrls: ['./financial-info.component.scss'],
    standalone: true,
    imports: [
        FormsModule,
        CommonModule,
        MatIconModule,
        TranslateModule,
        ReactiveFormsModule,
        SelectGeneralApplicationComponent,
        InputGeneralApplicationComponent,
        InputSelectGeneralComponent,
        MatRadioModule,
        RevampSelectGeneralApplicationComponent,
        RevampInputGeneralApplicationComponent,
        MatDividerModule,
        LoaderComponent,
    ],
    styles: [
        `
            :host {
                width: 100%;
            }
        `,
    ],
})
export class FinancialInfoComponent {
    private readonly destroy$ = new Subject<void>();
    isRefresh = false;
    isLoading = false;
    isRefreshLoading = false;
    @Input() productName: string;
    product: any;
    prodValidation: any;
    maxTenureLength: any;
    prodCode;
    getCaseStatusResponse: boolean = false;
    isDuplicateApp: boolean = false;
    duplicateAppNo: any;
    duplicateProdCode: any[] = [];
    financingInfoForm: FormGroup;
    isRequired: any;
    submitted: boolean = false;
    toggleDropDown = [false, false, false];
    showTenure: boolean = true;
    purposeOfFinancing: string = '';
    @Input() close: any;
    @ViewChild('formContainer') formContainer!: ElementRef;

    statusOptions = [
        'Referred',
        'Application Submission',
        'Approval',
        'Customer Acceptance',
        'Disbursement',
        'P2P Hosting',
    ];
    selectedTenureTab: 'month' | 'year' = 'month';
    monthYear = monthYearLabel;
    tenureUnit: string = 'months';
    PurposeOfFinancingOptions = [
        {
            value: 'WC',
            label: 'COMMON.WORKING_CAPITAL',
        },
        {
            value: 'CF',
            label: 'COMMON.CONTRACT_FINANCING',
        },
        {
            value: 'IF',
            label: 'COMMON.INVOICE_FINANCING',
        },
    ];

    dgpPofOPtions = {
        IF: {
            value: 'IF',
            label: 'COMMON.INVOICE_FINANCING',
        },
        TF: {
            value: 'TF',
            label: 'COMMON.TERM_FINANCING',
        },
        RC: {
            value: 'RC',
            label: 'COMMON.REVOLVING_CREDIT',
        },
        WC: {
            value: 'WC',
            label: 'COMMON.WORKING_CAPITAL',
        },
        CF: {
            value: 'CF',
            label: 'COMMON.CONTRACT_FINANCING',
        },
    };

    tenure: string = '';
    tenureOptions = tenureOptionsMonths;
    businessType: string = '';
    businessTypeOptions = [
        {
            value: 'SP',
            label: 'COMMON.SOLE_PROPRIETORSHIP',
        },
        {
            value: 'PN',
            label: 'COMMON.PARTNERSHIP',
        },
        {
            value: 'LLP',
            label: 'COMMON.LIMITED_LIABILITY',
        },
        {
            value: 'SDNBHD',
            label: 'COMMON.SDNBHD',
        },
        {
            value: 'PROFBODIES',
            label: 'COMMON.PROFESSIONAL_SERVICE_PROVIDER',
        },
        {
            value: 'IND',
            label: 'COMMON.INDIVIDUAL',
        },
    ];
    yesNoOptionType: string = '';
    bumiputeraType: string = '';
    stateName: string = '';
    stateOptions = [];
    cityName = '';
    cityOptions = [];
    yesNoOptions = [
        {
            value: 'Yes',
            label: 'FINANCING_STEPS.SABAH_SARAWAK_OR_BUMIPUTERA_YES',
        },
        {
            value: 'No',
            label: 'FINANCING_STEPS.SABAH_SARAWAK_OR_BUMIPUTERA_NO',
        },
    ];
    directorsList = [];
    ownersList = [];
    partiesList = [];
    selectedDirector: any;
    selectedOwner: any;
    selectedParty: any;
    selectedDirectorIndex: number | null = null;
    selectedOwnerIndex: number | null = null;
    selectedPartyIndex: number | null = null;
    bumiputeraValue: any;
    isDraft: any;
    caseId: any;
    private _fromChatbot = false;
    private _idaStartNewHandoff = false;
    /**
     * iDA handoff payload, read out of localStorage / the `#ida=` hash early in
     * initAfterCaseId() but applied only once the draft has loaded.
     *
     * Reading and applying are split deliberately. The read has to happen
     * synchronously so the hash is consumed before anything else can clear it;
     * the apply has to happen AFTER setFormValues(draftData), which blanket
     * patches every Step 1 control with `|| ''` fallbacks and would otherwise
     * wipe the iDA values with those of whatever draft the portal picked up.
     */
    private _idaData: Record<string, string> | null = null;
    showVerifyCompanyRegNoBtn: boolean = false;
    showVerifyIdentificationNoBtn: boolean = true;
    showDirectorList: boolean = false;
    showOwnersList: boolean = false;
    showPartiesList: boolean = false;
    showApplicantDetails: boolean = true;
    directorsDesignation = [];
    directorsName = [];
    ownersName = [];
    partiesName = [];
    directorName: any;
    radioSelection: boolean = false;
    companyRegNoVerifyLoading: boolean = false;
    identificationNoLoading: boolean = false;
    isDGP: boolean = false;
    showVerifyIdentificationNo: boolean = false;
    showBRNVerified: boolean = false;
    showKYCVerified: boolean = false;
    disableVerifyIdentificationNo: boolean = true;
    disableApplicantName: boolean = false;
    companyName: any;
    fromResume: boolean = false;
    showCompanyRegNo: boolean = false;
    showIndividualNRICName: boolean = false;
    showMgtNRICName: boolean = false;
    showIndividualNRICNo: boolean = false;
    showCompanyNRICName: boolean = false;
    financingPurpose: string = '';
    digitOnlyError;
    companyRegNoError: string;
    companyRegNoError2: string;
    identificationNoError: string;
    individualIdentificationNoError: string;
    individualNRICNameError: string;
    mgtNRICNameError: string;
    companyNRICNameError: string;
    minMaxPrefAmountError: string;
    tenureError: string;
    tenureLable: string;
    tenureDuration: string;
    tenureMinMaxLable: string;
    maxAgeError: string;
    minAgeError: string;
    ageRequiredError: string;
    businessAddress1Error: string;
    postCodeError: string;
    stateError: string;
    verifyCompanyRegNoError: string;
    verifyIdentificationNoError: string;
    selectDirectorError: string;
    selectOwnerError: string;
    selectPartyError: string;
    invalidStepError: string;
    disableCompanyRegNo: boolean = false;
    disableIdentificationNo: boolean = false;
    checkStatus: any;
    maxAge = 2;
    maxCompanyRegNo = 20;
    maxPreferredAmount = 12;
    maxIdentificationNo = 12;
    maxIndividualIdNo = 12;
    maxIndividualNRICName = 50;
    maxMgtNRICName = 30;
    maxCompanyNRICName = 50;
    maxBusinessAddress1 = 50;
    maxBusinessAddress2 = 50;
    maxPostCode = 5;
    closeTostr: string;
    disableSubmitBtn: boolean = false;
    checkMatchingProduct: boolean = false;
    successVerifyIdentification: boolean = false;
    showCities: boolean = false;
    response;
    showIdentificationNo = false;
    financingAmountError: any;
    filteredOptions: any = [];
    userData: any = null;
    regBrnNum: any = null;

    rightSide = [
        {
            title: 'RIGHT_DIV.TITLE_1',
            subtitle: 'RIGHT_DIV.SUBTITLE_1',
        },
        {
            title: 'RIGHT_DIV.TITLE_2',
            subtitle: 'RIGHT_DIV.SUBTITLE_2',
        },
        {
            title: 'RIGHT_DIV.TITLE_3',
            subtitle: 'RIGHT_DIV.SUBTITLE_3',
        },
    ];

    fieldInfo = [
        {
            imgUrl: '../../../../../assets/images/CurrencyDollar.png',
            que: 'FEILD_INFO.WC_QUE',
            ans: 'FEILD_INFO.WC_ANS',
        },
        {
            imgUrl: '../../../../../assets/images/document-filled.png',
            que: 'FEILD_INFO.CF_QUE',
            ans: 'FEILD_INFO.CF_ANS',
        },
        {
            imgUrl: '../../../../../assets/images/Invoice.png',
            que: 'FEILD_INFO.IF_QUE',
            ans: 'FEILD_INFO.IF_ANS',
        },
    ];

    isModalOpen: boolean = false;
    showOldBizRegNo: boolean = false;

    constructor(
        private translate: TranslateService,
        private fb: FormBuilder,
        public dialog: MatDialog,
        private router: Router,
        private _dashboardService: ApplicationDashboardService,
        private _userService: DashboardService,
        private applicationService: ApplicationService,
        private toastr: ToastrService,
        private idaMessageBridgeService: IdaMessageBridgeService,
        private activatedRoute: ActivatedRoute,
    ) {
        // If resume journey from draft page
        this.caseId = this.router.getCurrentNavigation().extras.state?.caseId;
        if (!this.caseId) {
            //forwarded from apply product
            this.caseId =
                this.router.getCurrentNavigation().extras.state?.setProductDetails;
        }
        const query = new URLSearchParams(window.location.search);
        // Check query param for caseId (from chatbot or direct link)
        if (!this.caseId) {
            const qCaseId = query.get('caseId');
            if (qCaseId) {
                this.caseId = qCaseId;
            }
        }
        // If no caseId and no router state at all (full-page navigation e.g.
        // from chatbot redirect), create a new case instead of bouncing.
        const hasRouterState =
            !!this.router.getCurrentNavigation()?.extras?.state;
        if (!this.caseId && !hasRouterState) {
            // Don't redirect — ngOnInit will create the case via createcase API
            this._fromChatbot = true;
            // If iDA passed Step 1/2 payload, this is a Start New handoff.
            // Request backend override so any existing draft does not lock us
            // at a later step (which causes "Invalid step" on Step 1 actions).
            this._idaStartNewHandoff = this.hasIdaHandoffPayload();
        } else {
            //redirect to products if directly accessing the URL
            this.applicationService.checkCaseIdForRedirection(this.caseId);
        }

        if (!this.productName) {
            this.productName =
                this.router.getCurrentNavigation().extras.state?.productName;
        }

        //For check matching products flow
        this.checkMatchingProduct =
            this.router.getCurrentNavigation().extras.state?.checkMatchingProduct;

        //If resume journey from draft page or product listing page
        this.fromResume =
            this.router.getCurrentNavigation().extras.state?.fromResume;

        if (!this.fromResume) {
            const qFromResume = query.get('fromResume');
            this.fromResume = qFromResume === '1' || qFromResume === 'true';
        }

        if (
            this.checkMatchingProduct === undefined ||
            this.checkMatchingProduct === null
        ) {
            const qCheckMatching = query.get('checkMatchingProduct');
            const qProductFlow = query.get('productFlow');

            if (qCheckMatching === '1' || qCheckMatching === 'true') {
                this.checkMatchingProduct = true;
            } else if (qCheckMatching === '0' || qCheckMatching === 'false') {
                this.checkMatchingProduct = false;
            } else if (qProductFlow === 'matched') {
                this.checkMatchingProduct = true;
            } else if (qProductFlow === 'select') {
                this.checkMatchingProduct = false;
            }
        }

        // Chatbot handoff never picks a product (no setproduct call), so it
        // must follow the "check matching products" path rather than the
        // default "specific product already selected" path — otherwise Step 2
        // moves straight to checkEligibility with no prodCode and 500s.
        if (
            this._fromChatbot &&
            (this.checkMatchingProduct === undefined ||
                this.checkMatchingProduct === null)
        ) {
            this.checkMatchingProduct = true;
        }

        this.userData = this._userService.getUser();
    }

    setFIOptions(pof: string, draft: string | null = null) {
        const pofOpt = pof.split('|');
        pofOpt.forEach((item: any) => {
            this.filteredOptions.push(this.dgpPofOPtions[item]);
        });
        this.PurposeOfFinancingOptions = this.filteredOptions;
        return draft
            ? this.dgpPofOPtions?.[draft]?.value
            : this.filteredOptions?.[0]?.value;
    }

    ngOnInit(): void {
        this.updateTranslation();
        this.financingInfoForm = this.createFinancingInfoForm();

        // iDA chatbot handoff: if no caseId, create one via createcase API
        if (this._fromChatbot && !this.caseId) {
            this.isLoading = true;
            const createCasePayload = this._idaStartNewHandoff
                ? { deleteAndContinue: true }
                : {};
            this.applicationService
                .createCaseProduct(createCasePayload)
                .subscribe({
                    next: (res: any) => {
                        const response = res;
                        // For Start New handoff, prefer fresh-case shapes first.
                        if (this._idaStartNewHandoff) {
                            if (response.caseId) {
                                this.caseId = response.caseId;
                            } else if (
                                Array.isArray(response) &&
                                response.length > 0
                            ) {
                                this.caseId = response[0].caseId;
                            } else if (
                                response.drafts &&
                                response.drafts.length > 0
                            ) {
                                this.caseId = response.drafts[0].caseId;
                            }
                        } else if (
                            response.drafts &&
                            response.drafts.length > 0
                        ) {
                            this.caseId = response.drafts[0].caseId;
                        } else if (
                            Array.isArray(response) &&
                            response.length > 0
                        ) {
                            this.caseId = response[0].caseId;
                        } else if (response.caseId) {
                            this.caseId = response.caseId;
                        }
                        if (this.caseId) {
                            try {
                                const idaData = JSON.parse(
                                    localStorage.getItem(
                                        'ida-application-data',
                                    ) || '{}',
                                );
                                idaData.caseId = this.caseId;
                                localStorage.setItem(
                                    'ida-application-data',
                                    JSON.stringify(idaData),
                                );
                            } catch {
                                /* ignore */
                            }
                            this.isLoading = false;
                            this.initAfterCaseId();
                        } else {
                            this.router.navigate(['/business-financing']);
                        }
                    },
                    error: () => {
                        this.router.navigate(['/business-financing']);
                    },
                });
        } else {
            this.initAfterCaseId();
        }
    }

    private hasIdaHandoffPayload(): boolean {
        try {
            if (window.location.hash?.startsWith('#ida=')) {
                return true;
            }
        } catch {
            /* ignore */
        }

        try {
            const raw = localStorage.getItem('ida-application-data');
            const ts = localStorage.getItem('ida-application-data-timestamp');
            if (!raw) {
                return false;
            }

            // Ignore stale payloads from old sessions.
            const ageMs = ts ? Date.now() - Number.parseInt(ts, 10) : NaN;
            if (!Number.isFinite(ageMs) || ageMs > 15 * 60 * 1000) {
                return false;
            }

            const parsed = JSON.parse(raw);
            return (
                !!parsed &&
                typeof parsed === 'object' &&
                Object.keys(parsed).length > 0
            );
        } catch {
            return false;
        }
    }

    private initAfterCaseId(): void {
        // Existing draft resume must mirror the portal draft flow exactly:
        // use case data only and never overlay iDA handoff payload values.
        if (this.fromResume) {
            this.clearIdaHandoffData();
        } else {
            // Consume the iDA payload before the async draft load below can settle.
            // Applied later, in the finally() of that same chain — see _idaData.
            this.readIdaApplicationData();
        }

        let draftData: any = {};
        this.getCaseDetails(this.caseId)
            .then((data) => {
                draftData = data;
                if (data.prodSelectionList?.length > 0) {
                    this.product = data.prodSelectionList?.[0];
                    this.prodCode = this.product.prodCode;
                    if (this?.product) {
                        if (this?.product?.productType === 'DGP') {
                            this.isDGP = true;
                            this.prodValidation = JSON.parse(
                                this.product?.productValidation,
                            );

                            if (this.prodValidation?.pof?.includes('TF')) {
                                this.showTenure = true;
                            } else if (
                                this.prodValidation?.pof?.includes('RC') ||
                                this.prodValidation?.pof?.includes('IF')
                            ) {
                                this.showTenure = false;
                            }

                            if (this.prodValidation?.pof?.includes('IF')) {
                                this.financingInfoForm.patchValue({
                                    financingPurpose: 'IF',
                                });
                            }
                            this.maxTenureLength =
                                this.prodValidation?.tenure?.max?.toString().length;

                            this.financingInfoForm.controls[
                                'tenure'
                            ].setValidators([
                                Validators.min(
                                    this.prodValidation?.tenure?.min,
                                ),
                                Validators.max(
                                    this.prodValidation?.tenure?.max,
                                ),
                            ]);

                            this.tenureMinMaxLable = ` (${this.prodValidation?.tenure?.min} min - ${this.prodValidation?.tenure?.max} max)`;

                            let unit = this.prodValidation?.tenure?.unit;
                            switch (unit) {
                                case 'days':
                                    this.tenureDuration =
                                        'NEW_APPLICATION.PREFERRED_INSTALLMENT_TIME_D';
                                    break;
                                case 'months':
                                    this.tenureDuration =
                                        'NEW_APPLICATION.PREFERRED_INSTALLMENT_TIME_M';
                                    break;
                                case 'years':
                                    this.tenureDuration =
                                        'NEW_APPLICATION.PREFERRED_INSTALLMENT_TIME_Y';
                                    break;
                                default:
                                    this.tenureDuration =
                                        'NEW_APPLICATION.PREFERRED_INSTALLMENT_TIME_Y';
                                    break;
                            }

                            draftData.purposeOfFinancing = this.setFIOptions(
                                this.prodValidation.pof,
                                draftData.purposeOfFinancing,
                            );
                        }
                    }
                }
                if (
                    draftData.qType === 'LOAN_CGCP_CHK_CNSN' ||
                    draftData.qType === 'LOAN_CGC_CHK_CNSN'
                ) {
                    //Check if BRN is verified if coming from draft/refresh
                    this.checkStatus = 'BRN';
                } else if (
                    draftData.qType === 'LOAN_CGCP_USR_INFO' ||
                    draftData.qType === 'LOAN_CGC_USR_INFO'
                ) {
                    //Check if KYC/NRIC is verified if coming from draft/refresh
                    this.checkStatus = 'NRIC';
                } else if (
                    draftData.qType === 'LOAN_CGCP_UPD_USR_INFO' ||
                    draftData.qType === 'LOAN_CGC_UPD_USR_INFO'
                ) {
                    //Check if KYC/NRIC is verified if coming from draft/refresh
                    this.checkStatus = 'NRIC';
                }

                this.setFormValues(draftData);
            })
            // getCaseDetails() rejects on an HTTP error, and also throws on an
            // empty response (it returns value[0]). Swallow it so the finally()
            // below still runs: a failed draft fetch must not cost the user the
            // data they just gave iDA.
            .catch(() => {
                /* no draft details — nothing to merge with */
            })
            .finally(() => {
                // Only non-resume handoff should apply chatbot payload.
                if (!this.fromResume) {
                    // Ordered AFTER setFormValues on purpose. iDA is the more recent
                    // source of truth: the user typed those answers into the chatbot
                    // seconds ago, whereas the draft is whatever case the portal
                    // happened to adopt (createcase returns existing drafts, and
                    // ngOnInit takes drafts[0]). Applying iDA last makes it win.
                    this.applyIdaApplicationData();
                }
            });

        this.getStatesList();

        this.translate.get('ERROR.IS_REQUIRED').subscribe((data: any) => {
            this.isRequired = data;
        });

        this.translate.get('ERROR.DIGIT_ONLY').subscribe((data: any) => {
            this.digitOnlyError = data;
        });

        this.translate
            .get('ERROR.COMPANY_REG_NO_ERROR_1')
            .subscribe((data: any) => {
                this.companyRegNoError = data;
            });

        this.translate
            .get('ERROR.COMPANY_REG_NO_ERROR_2')
            .subscribe((data: any) => {
                this.companyRegNoError2 = data;
            });

        this.translate
            .get('ERROR.IDENTIFICATION_NO_ERROR')
            .subscribe((data: any) => {
                this.identificationNoError = data;
            });

        this.translate
            .get('ERROR.IND_IDENTIFICATION_NO_ERROR')
            .subscribe((data: any) => {
                this.individualIdentificationNoError = data;
            });

        this.translate
            .get('ERROR.IND_NRIC_NAME_ERROR')
            .subscribe((data: any) => {
                this.individualNRICNameError = data;
            });

        this.translate
            .get('ERROR.MGT_NRIC_NAME_ERROR')
            .subscribe((data: any) => {
                this.mgtNRICNameError = data;
            });

        this.translate
            .get('ERROR.COMPANY_NRIC_NAME_ERROR')
            .subscribe((data: any) => {
                this.companyNRICNameError = data;
            });

        this.translate.get('ERROR.PREF_AMOUNT').subscribe((data: any) => {
            this.minMaxPrefAmountError = data;
        });

        this.translate.get('ERROR.TENURE_ERROR').subscribe((data: any) => {
            this.tenureError = data
                .replace('MIN', this.prodValidation?.tenure?.min)
                .replace('MAX', this.prodValidation?.tenure?.max)
                .replace('UNIT', this.prodValidation?.tenure?.unit);
        });

        this.translate
            .get('NEW_APPLICATION.PREFERRED_INSTALLMENT_TIME')
            .subscribe((data: any) => {
                this.tenureLable = data;
            });

        this.translate.get('ERROR.MAX_AGE').subscribe((data: any) => {
            this.maxAgeError = data;
        });

        this.translate.get('ERROR.MIN_AGE').subscribe((data: any) => {
            this.minAgeError = data;
        });

        this.translate.get('ERROR.AGE').subscribe((data: any) => {
            this.ageRequiredError = data;
        });

        this.translate.get('ERROR.BUSSINESS_LINE_1').subscribe((data: any) => {
            this.businessAddress1Error = data;
        });

        this.translate.get('ERROR.POST_CODE').subscribe((data: any) => {
            this.postCodeError = data;
        });

        this.translate
            .get('ERROR.VERIFY_COMPANY_REG_NO')
            .subscribe((data: any) => {
                this.verifyCompanyRegNoError = data;
            });

        this.translate
            .get('ERROR.VERIFY_IDENTIFICATION_NO')
            .subscribe((data: any) => {
                this.verifyIdentificationNoError = data;
            });

        this.translate.get('ERROR.SELECT_DIRECTOR').subscribe((data: any) => {
            this.selectDirectorError = data;
        });

        this.translate.get('ERROR.SELECT_OWNER').subscribe((data: any) => {
            this.selectOwnerError = data;
        });

        this.translate.get('ERROR.SELECT_PARTY').subscribe((data: any) => {
            this.selectPartyError = data;
        });

        this.translate.get('COMMON.CLOSE').subscribe((data: any) => {
            this.closeTostr = data;
        });

        this.translate.get('ERROR.INVALID_STEP').subscribe((data: any) => {
            this.invalidStepError = data;
        });

        this.initIdaAutofillListener();
    }

    private createFinancingInfoForm(): FormGroup {
        return this.fb.group({
            financingPurpose: ['', Validators.required],
            prefferedAmount: ['', Validators.required],
            tenure: ['', [Validators.required]],
            businessType: ['', [Validators.required]],
            companyRegNo: [''],
            oldCompanyRegNo: [''],
            directorsList: [''],
            ownersList: [''],
            partiesList: [''],
            identificationNo: [''],
            sabahSarwak: ['', [Validators.required]],
            bumiputeraStatus: ['', [Validators.required]],
            age: [
                '',
                [
                    Validators.required,
                    Validators.min(18),
                    Validators.maxLength(2),
                ],
            ],
            individualNRICName: [''],
            mgtNRICName: [''],
            individualNRICNo: [''],
            companyNRICName: [''],
            businessAddress1: ['', [Validators.required]],
            businessAddress2: [''],
            state: ['', [Validators.required]],
            city: ['', [Validators.required]],
            postCode: ['', [Validators.required, Validators.minLength(5)]],
            director: [''],
            party: [''],
            owner: [''],
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * Pre-fill this form from data collected by the iDA chatbot before it
     * redirected here (see docs/PORTAL_HANDOFF_APPLICATION_DATA_PREFILL.md
     * in aws-repo-ssv-ida-agentic).
     *
     * iDA writes to localStorage (key: ida-application-data) rather than
     * using a live postMessage, because the chatbot handoff navigates via
     * window.top.location.href — a full page reload that tears down the
     * iframe and any postMessage listener before this page ever loads. A
     * live message sent at that moment would never be received.
     * localStorage on the shared parent origin survives the reload.
     *
     * Values arriving from iDA are already normalized to this portal's
     * own codes (WC/CF/IF for purpose, SP/PN/LLP/SDNBHD/PROFBODIES/IND for
     * business type, "01"-"10" for sector, "Yes"/"No" for
     * Sabah/Sarawak and Bumiputera) — see APPLICATION_FLOW_API.md for the
     * authoritative code tables iDA maps from.
     */
    private readIdaApplicationData(): void {
        // First check URL hash for data passed from chatbot iframe (cross-origin)
        let raw = localStorage.getItem('ida-application-data');
        if (!raw) {
            try {
                const hash = window.location.hash;
                if (hash.startsWith('#ida=')) {
                    raw = decodeURIComponent(hash.substring(5));
                    // Clean the hash from URL without triggering navigation
                    history.replaceState(
                        null,
                        '',
                        window.location.pathname + window.location.search,
                    );
                }
            } catch {
                /* ignore */
            }
        }
        if (!raw) {
            return;
        }

        // Always clear immediately after reading, whether or not parsing
        // succeeds, so a malformed or stale entry from a previous
        // conversation never gets reapplied on a later unrelated visit to
        // this page.
        localStorage.removeItem('ida-application-data');
        localStorage.removeItem('ida-application-data-timestamp');

        let data: Record<string, string>;
        try {
            data = JSON.parse(raw);
        } catch {
            return;
        }
        if (!data || typeof data !== 'object') {
            return;
        }

        this._idaData = data;
    }

    private clearIdaHandoffData(): void {
        try {
            localStorage.removeItem('ida-application-data');
            localStorage.removeItem('ida-application-data-timestamp');
            localStorage.removeItem('ida-business-info-data');
            localStorage.removeItem('ida-business-info-data-timestamp');
        } catch {
            /* localStorage unavailable */
        }

        this._idaData = null;
    }

    /**
     * Apply the payload parked by readIdaApplicationData() to this form.
     *
     * Called from the finally() of the getCaseDetails() chain in
     * initAfterCaseId(), i.e. strictly after setFormValues(draftData), so iDA
     * values overwrite draft values rather than the other way round.
     */
    private applyIdaApplicationData(): void {
        const data = this._idaData;
        if (!data) {
            return;
        }
        this._idaData = null;

        const patch: Record<string, any> = {};

        if (data.purposeOfFinancing) {
            // `financingPurpose` is what the dropdown renders from
            // ([currentValue] in the template); the form control alone drives
            // validation only. Set both, or the field looks unanswered even
            // though iDA collected it. Same reason applies to stateName and
            // bumiputeraType below. iDA already sends WC/CF/IF, which match
            // PurposeOfFinancingOptions verbatim.
            this.financingPurpose = data.purposeOfFinancing;
            patch.financingPurpose = data.purposeOfFinancing;
        }
        if (data.financingAmount) {
            patch.prefferedAmount = data.financingAmount;
        }
        if (data.businessType) {
            // Run the same handler a manual dropdown selection would, so the
            // type-dependent fields actually become visible: the BRN input for
            // company types, the IC name/number inputs for Individual. Patching
            // businessType alone leaves showCompanyRegNo / showIndividualNRICName
            // false, which would hide the identification number we prefill below.
            //
            // This MUST run before the patchValue at the end of this method: it
            // resets companyRegNo, identificationNo, individualNRICNo and
            // sabahSarwak, and clears companyName. The blocks that follow
            // re-establish those values afterwards.
            this.handleBusinessTypeOptions(data.businessType);
            patch.businessType = data.businessType;
        }
        if (data.sabahSarawak === 'Yes' || data.sabahSarawak === 'No') {
            this.yesNoOptionType = data.sabahSarawak;
            patch.sabahSarwak = data.sabahSarawak;
            this.showSabahSarwakFields(data.sabahSarawak);
        }
        if (data.bumiputeraStatus === 'Yes' || data.bumiputeraStatus === 'No') {
            // Two distinct representations, both required: `bumiputeraValue`
            // is the 'Y'/'N' code sent in the financinginfo payload, while
            // `bumiputeraType` is the 'Yes'/'No' value the dropdown displays
            // and matches yesNoOptions.
            this.bumiputeraValue = data.bumiputeraStatus === 'Yes' ? 'Y' : 'N';
            this.bumiputeraType = data.bumiputeraStatus;
            patch.bumiputeraStatus = data.bumiputeraStatus;
        }
        if (data.age) {
            patch.age = data.age;
        }
        if (data.businessAddress1) {
            patch.businessAddress1 = data.businessAddress1;
        }
        if (data.businessAddress2) {
            patch.businessAddress2 = data.businessAddress2;
        }
        if (data.state) {
            // Mirrors onSelectState, minus the city reset: the City dropdown is
            // hidden behind *ngIf="showCities" and its options are fetched
            // lazily per state, so patching the control alone would leave State
            // blank and City missing entirely. iDA sends the state label (e.g.
            // "Johor"), which is exactly what getStates() returns as both value
            // and label, so it can be used for the lookup as-is.
            //
            // The city value itself is prefilled by the data.city block below,
            // which is why cityName/city are deliberately NOT cleared here.
            this.stateName = data.state;
            patch.state = data.state;
            this.showCities = true;
            this.cityOptions = [];
            this.getCitiesList(data.state);
        }
        if (data.postCode) {
            patch.postCode = data.postCode;
        }
        // tenureUnit ("M"/"Y") + tenureValue (the number) map to this
        // component's own tenureUnit/tenureOptions state, not directly to a
        // form control value, since the tenure dropdown options differ
        // between months (1-11) and years (1-10).
        if (data.tenureUnit === 'M' || data.tenureUnit === 'Y') {
            if (data.tenureUnit === 'M') {
                this.selectedTenureTab = 'month';
                this.tenureUnit = 'months';
                this.tenureOptions = tenureOptionsMonths;
            } else {
                this.selectedTenureTab = 'year';
                this.tenureUnit = 'years';
                this.tenureOptions = tenureOptionsYears;
            }
            if (data.tenureValue) {
                this.tenure = data.tenureValue;
                patch.tenure = data.tenureValue;
            }
        }

        if (data.city) {
            this.cityName = data.city;
            patch.city = data.city;
        }

        // iDA sends the business registration number and the applicant's own IC
        // as two separate keys — `regNo` and `directorNric` — matching the names
        // the draft payload already uses. Every constitution type except
        // Individual needs BOTH, which is why a single field could never fill
        // this form correctly.
        //
        // `identificationNumber` is the older single-field key, kept as a
        // fallback so a not-yet-redeployed agent still prefills something. It
        // always carried the BRN for company types and the IC for Individual.
        const idaRegNo =
            data.regNo ||
            (data.businessType !== 'IND'
                ? data.identificationNumber
                : undefined);
        const idaPersonalIc =
            data.directorNric ||
            (data.businessType === 'IND'
                ? data.identificationNumber
                : undefined);

        // Both guards below are derived from the payload rather than from
        // showCompanyRegNo / showIndividualNRICNo. Those flags say the same
        // thing, but only once showSabahSarwakFields has run — which happens in
        // the sabahSarawak block above and therefore only when that key is
        // present. Reading the data directly keeps the prefill correct
        // regardless of call order or a missing sabahSarawak.

        // Every constitution type except Individual has a BRN field. Pre-fills
        // only — does NOT mark it verified. The user still has to run "Sahkan" /
        // checkbrn, since iDA cannot verify it.
        if (idaRegNo && data.businessType !== 'IND') {
            patch.companyRegNo = idaRegNo;
        }

        // The set of types that type their own IC and names directly, rather
        // than having them come back from the SSM lookup. Same set drives
        // showIndividualNRICNo and showCompanyNRICName.
        const usesDirectInputs =
            data.businessType === 'IND' ||
            data.businessType === 'LLP' ||
            data.businessType === 'PROFBODIES' ||
            ((data.businessType === 'SP' || data.businessType === 'PN') &&
                data.sabahSarawak === 'Yes');

        // iDA collects the applicant's IC for EVERY business type, but the form
        // has two different destinations for it:
        //   - usesDirectInputs set       -> individualNRICNo (plain input)
        //   - Sdn Bhd, SP/PN outside S/S -> identificationNo, the control that
        //                                   feeds checkkyc as directorNric
        // Prefilling identificationNo is worthwhile rather than cosmetic:
        // verifying the BRN does not clear it, because onVerifyCompanyRegNo
        // resets only director/owner/party. The value survives the user clicking
        // Sahkan and is still there for the KYC check.
        if (idaPersonalIc) {
            if (usesDirectInputs) {
                patch.individualNRICNo = idaPersonalIc;
            } else {
                patch.identificationNo = idaPersonalIc;
            }
        }

        // Applicant name has two possible destinations, and both feed the same
        // `snsAppName` field on submit:
        //   - Individual          -> individualNRICName
        //   - LLP / Professional Services / SP+PN registered in Sabah/Sarawak
        //                         -> mgtNRICName ("Applicant Name" in the UI)
        // Sdn Bhd and SP/PN outside Sabah/Sarawak get neither: the name is
        // picked from the SSM director/owner/partner list after checkbrn.
        //
        // Previously everything went to individualNRICName, which is only
        // rendered and only submitted for Individual, so company-type applicants
        // saw an empty Applicant Name and had to retype it.
        const applicantFullName = data.fullNameAsPerIC || data.applicantName;
        if (applicantFullName) {
            if (data.businessType === 'IND') {
                patch.individualNRICName = applicantFullName;
            } else if (usesDirectInputs) {
                patch.mgtNRICName = applicantFullName;
            }
        }

        // `companyName` is the portal's read-only BRN verification result and
        // must remain owned by the existing checkBRN flow. The editable control
        // submitted as `snsBizName` is `companyNRICName`, so iDA only prefills
        // that control for business types that use direct inputs.
        //
        // iDA skips Company Name for Individual, so in practice this only fires
        // for the other company types in usesDirectInputs.
        if (data.businessName && usesDirectInputs) {
            patch.companyNRICName = data.businessName.toUpperCase();
        }

        // Step 2 (Business Information) is collected by iDA as well, but this
        // component only owns Step 1. The main payload is deleted above so a
        // stale entry can never be reapplied here, so pass the Step 2 subset
        // forward under its own key for BusinessInfoComponent to consume.
        this.stashIdaBusinessInfoData(data);

        if (Object.keys(patch).length > 0) {
            this.financingInfoForm.patchValue(patch, { emitEvent: false });
        }
    }

    /**
     * Carry the Business Information (Step 2) subset of the iDA payload forward
     * to BusinessInfoComponent.
     *
     * iDA collects Steps 1 and 2 conversationally, then hands off to
     * /corporate. This component owns Step 1 only, and it deletes
     * `ida-application-data` the moment it reads it so a stale conversation can
     * never be reapplied. That would strand the Step 2 values, so they get
     * re-saved under their own key which BusinessInfoComponent consumes and
     * clears in turn.
     */
    private stashIdaBusinessInfoData(data: Record<string, string>): void {
        const step2Keys = [
            'sector',
            'yearsInOperation',
            'profitability',
            'annualSalesTurnover',
            'paidUpCapital',
            'profitBeforeInterest',
            'monthlyInstallment',
            'fulltimeEmployee',
            'knowImsmeFrom',
            'comment',
        ];

        const subset: Record<string, string> = {};
        step2Keys.forEach((key) => {
            const value = data[key];
            if (value !== undefined && value !== null && value !== '') {
                subset[key] = value;
            }
        });

        if (Object.keys(subset).length === 0) {
            return;
        }

        try {
            localStorage.setItem(
                'ida-business-info-data',
                JSON.stringify(subset),
            );
            localStorage.setItem(
                'ida-business-info-data-timestamp',
                Date.now().toString(),
            );
        } catch {
            /* localStorage unavailable — Step 2 simply won't prefill */
        }
    }

    private initIdaAutofillListener(): void {
        this.idaMessageBridgeService.userInput$
            .pipe(takeUntil(this.destroy$))
            .subscribe((event) => {
                this.applyIdaPayload(event?.value, event?.field);
            });

        this.idaMessageBridgeService.botResponse$
            .pipe(takeUntil(this.destroy$))
            .subscribe((event) => {
                this.applyIdaPayload(event?.message);
            });
    }

    private applyIdaPayload(value: unknown, explicitField?: string): void {
        if (!this.financingInfoForm) {
            return;
        }

        if (
            explicitField &&
            this.financingInfoForm.get(explicitField) &&
            (typeof value === 'string' || typeof value === 'number')
        ) {
            this.patchSingleControl(explicitField, value);
            return;
        }

        if (value && typeof value === 'object' && !Array.isArray(value)) {
            this.patchKnownControls(value as Record<string, unknown>);
            return;
        }

        if (typeof value === 'string' || typeof value === 'number') {
            this.patchSingleControl('companyRegNo', value);
        }
    }

    private patchKnownControls(payload: Record<string, unknown>): void {
        const patchValues: Record<string, string> = {};

        Object.keys(payload).forEach((key) => {
            if (!this.financingInfoForm.get(key)) {
                return;
            }

            const value = payload[key];
            if (typeof value === 'string' || typeof value === 'number') {
                patchValues[key] = String(value).trim();
            }
        });

        if (Object.keys(patchValues).length > 0) {
            this.financingInfoForm.patchValue(patchValues, {
                emitEvent: false,
            });
        }
    }

    private patchSingleControl(
        controlName: string,
        value: string | number,
    ): void {
        const control = this.financingInfoForm.get(controlName);
        if (!control) {
            return;
        }

        control.patchValue(String(value).trim(), {
            emitEvent: false,
        });
    }

    get f() {
        return this.financingInfoForm.controls;
    }

    // keyPressNumbers(event) {
    //     let charCode = event.which ? event.which : event.keyCode;
    //     // Only Numbers 0-9
    //     if (charCode < 48 || charCode > 57) {
    //         event.preventDefault();
    //         return false;
    //     } else {
    //         return true;
    //     }
    // }

    updateTranslation() {
        this.translate
            .get('NEW_APPLICATION.PREFERRED_INSTALLMENT_TIME')
            .subscribe((data: any) => {
                this.tenureLable = data;
            });
        this.translate.get('ERROR.IS_REQUIRED').subscribe((data: any) => {
            this.isRequired = data;
        });

        this.translate.get('ERROR.DIGIT_ONLY').subscribe((data: any) => {
            this.digitOnlyError = data;
        });

        this.translate
            .get('ERROR.COMPANY_REG_NO_ERROR_1')
            .subscribe((data: any) => {
                this.companyRegNoError = data;
            });

        this.translate
            .get('ERROR.COMPANY_REG_NO_ERROR_2')
            .subscribe((data: any) => {
                this.companyRegNoError2 = data;
            });

        this.translate.get('ERROR.PREF_AMOUNT').subscribe((data: any) => {
            this.minMaxPrefAmountError = data;
        });

        this.translate.get('ERROR.TENURE_ERROR').subscribe((data: any) => {
            this.tenureError = data
                .replace('MIN', this.prodValidation?.tenure?.min)
                .replace('MAX', this.prodValidation?.tenure?.max)
                .replace('UNIT', this.prodValidation?.tenure?.unit);
        });

        this.translate.get('ERROR.MAX_AGE').subscribe((data: any) => {
            this.maxAgeError = data;
        });

        this.translate.get('ERROR.MIN_AGE').subscribe((data: any) => {
            this.minAgeError = data;
        });

        this.translate.get('ERROR.AGE').subscribe((data: any) => {
            this.ageRequiredError = data;
        });

        this.translate.get('ERROR.BUSSINESS_LINE_1').subscribe((data: any) => {
            this.businessAddress1Error = data;
        });

        this.translate.get('ERROR.POST_CODE').subscribe((data: any) => {
            this.postCodeError = data;
        });

        this.translate
            .get('ERROR.VERIFY_COMPANY_REG_NO')
            .subscribe((data: any) => {
                this.verifyCompanyRegNoError = data;
            });

        this.translate
            .get('ERROR.VERIFY_IDENTIFICATION_NO')
            .subscribe((data: any) => {
                this.verifyIdentificationNoError = data;
            });

        this.translate.get('ERROR.SELECT_DIRECTOR').subscribe((data: any) => {
            this.selectDirectorError = data;
        });

        this.translate.get('COMMON.CLOSE').subscribe((data: any) => {
            this.closeTostr = data;
        });

        this.translate
            .get('ERROR.IND_NRIC_NAME_ERROR')
            .subscribe((data: any) => {
                this.individualNRICNameError = data;
            });

        this.translate
            .get('ERROR.MGT_NRIC_NAME_ERROR')
            .subscribe((data: any) => {
                this.mgtNRICNameError = data;
            });

        this.translate
            .get('ERROR.COMPANY_NRIC_NAME_ERROR')
            .subscribe((data: any) => {
                this.companyNRICNameError = data;
            });

        this.translate.get('ERROR.SELECT_OWNER').subscribe((data: any) => {
            this.selectOwnerError = data;
        });

        this.translate
            .get('ERROR.IDENTIFICATION_NO_ERROR')
            .subscribe((data: any) => {
                this.identificationNoError = data;
            });

        this.translate
            .get('ERROR.IND_IDENTIFICATION_NO_ERROR')
            .subscribe((data: any) => {
                this.individualIdentificationNoError = data;
            });

        this.translate
            .get('ERROR.FINANCING_AMOUNT_ERROR')
            .subscribe((data: any) => {
                this.financingAmountError = data;
            });
    }

    countCharacter(str: string, char: string): number {
        const regex = new RegExp(char, 'g');
        const matches = str.match(regex);
        return matches ? matches.length : 0;
    }

    keyPressNumbers(
        event: any,
        inputName?: string,
        isIdentificationNo?: boolean,
    ) {
        const element = event.target as HTMLInputElement;
        const inputValue = element.value;
        let numericValue = '';
        const initialCommas = this.countCharacter(inputValue, ',');
        let selectionStart = element.selectionStart || 0;
        const isBackspace = event.inputType === 'deleteContentBackward';

        if (inputName === 'isPrefAmount') {
            const rawValue = inputValue.replace(/[^0-9]/g, '');
            if (rawValue === '') {
                this.financingInfoForm.controls.prefferedAmount.patchValue(
                    null,
                );
                return (event.target.value = null);
            }

            numericValue = rawValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
            numericValue = `RM${numericValue}`;

            const addedCharacters = numericValue.length - inputValue.length;

            if (isBackspace) {
                const prevChar = inputValue[selectionStart - 1];
            } else {
                const prevChar = inputValue[selectionStart - 1];
                if (prevChar === ',' || prevChar === 'R') {
                    selectionStart += addedCharacters - 1;
                } else {
                    selectionStart += addedCharacters;
                }
            }
        } else {
            numericValue = inputValue.replace(/[^0-9]/g, '');
            if (
                (numericValue === '' || inputValue === '') &&
                inputName === 'age'
            ) {
                this.financingInfoForm.controls.age.patchValue(null);
            }
        }
        const finalCommas = this.countCharacter(numericValue, ',');
        if (isBackspace && finalCommas < initialCommas) {
            selectionStart -= 1;
        }
        element.value = numericValue;

        const finalCursorPos = Math.min(selectionStart, numericValue.length);
        element.setSelectionRange(finalCursorPos, finalCursorPos);

        if (isIdentificationNo) {
            const identificationNoAsNumber = Number(
                this.financingInfoForm.controls['identificationNo'].value,
            );
            if (Number.isNaN(identificationNoAsNumber)) {
                this.financingInfoForm.controls['identificationNo'].setValue(
                    null,
                );
            }
        }
    }

    onEnterCompanyNRICName(event) {
        event.target.value = event.target.value.toUpperCase();
    }

    restrictNumbers(event) {
        const inputValue = event.target.value;
        let allowedInputValue = '';
        allowedInputValue = inputValue.replace(/[0-9]/g, ''); // restrict numbers
        allowedInputValue = allowedInputValue === '' ? null : allowedInputValue;
        if (!allowedInputValue) {
            this.financingInfoForm.get('mgtNRICName').reset();
        }
        event.target.value = allowedInputValue;
    }

    pasteNumberOnly(event) {
        let clipboardData = event.clipboardData;
        let pastedData = clipboardData.getData('Text');
        if (isNaN(pastedData)) {
            event.preventDefault();
        } else {
            return;
        }
    }

    restrictNumbersOnPaste(event) {
        // restrict copy pasting numbers
        let clipboardData = event.clipboardData;
        let pastedData = clipboardData.getData('Text');
        if (isNaN(pastedData)) {
            return true;
        } else {
            event.preventDefault();
        }
    }

    mapOptionValue(value: any): any {
        if (value == null) {
            return null;
        } else {
            return value.toUpperCase() === 'Y' ? 'Yes' : 'No';
        }
    }

    convertToNumberFormat(num: string): string {
        return num.replace(/\D/g, '').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }

    handleMonthClick(event: Event) {
        this.selectedTenureTab = 'month';
        this.tenureUnit = 'months';
        this.tenureOptions = tenureOptionsMonths;
    }

    handleYearClick(event: Event) {
        this.selectedTenureTab = 'year';
        this.tenureUnit = 'years';
        this.tenureOptions = tenureOptionsYears;
    }

    setFormValues(data: any) {
        this.financingPurpose = data.purposeOfFinancing;
        this.tenure = String(data.tenureYears);

        this.businessType =
            data?.constitutionTypeStdCode ||
            this.userData?.details?.testconsttype ||
            '';
        this.setSelectSabahSarwak(
            this.mapOptionValue(
                data?.SabahSarawakRegistered || this.userData?.details?.sabah,
            ),
        );

        this.yesNoOptionType = this.mapOptionValue(
            data?.SabahSarawakRegistered || this.userData?.details?.sabah,
        );

        this.bumiputeraType = this.mapOptionValue(data?.bumiputeraStatus) || '';

        const prefferedAmountVal = data?.financingAmount;
        let formatPrefferedAmount = '';
        if (prefferedAmountVal) {
            formatPrefferedAmount = prefferedAmountVal;
            formatPrefferedAmount =
                formatPrefferedAmount === '' ? null : formatPrefferedAmount;
        }
        let regNo = '';
        let identificationNo = '';
        if (
            data.constitutionTypeStdCode == 'SDNBHD' ||
            (data.constitutionTypeStdCode == 'PN' &&
                this.yesNoOptionType == 'No') ||
            (data?.constitutionTypeStdCode == 'SP' &&
                this.yesNoOptionType == 'No')
        ) {
            if (this.checkStatus == 'BRN') {
                this.onVerifyCompanyRegNo(
                    true,
                    data?.regNo,
                    data?.SabahSarawakRegistered,
                );
                regNo = data?.regNo;
                if (data.oldBizRegNo) {
                    if (data.oldBizRegNo !== data.regNo) {
                        this.showOldBizRegNo = true;
                    }
                }
            } else if (this.checkStatus == 'NRIC') {
                if (data?.constitutionTypeStdCode == 'SDNBHD') {
                    this.directorsList.push({
                        Name: data.snsAppName,
                        Id: 0,
                    });
                    this.financingInfoForm.patchValue({ director: '0' });

                    this.companyName = data?.snsBizName;

                    this.showVerifyCompanyRegNoBtn = false;
                    this.showBRNVerified = true;
                    this.disableCompanyRegNo = true;
                    this.financingInfoForm.get('companyRegNo').disable();

                    this.showVerifyIdentificationNoBtn = false;
                    this.showVerifyIdentificationNo = true;
                    this.showKYCVerified = true;
                    this.successVerifyIdentification = true;
                    this.disableIdentificationNo = true;
                    this.financingInfoForm.get('identificationNo').disable();
                } else if (
                    data.constitutionTypeStdCode == 'SP' &&
                    this.yesNoOptionType == 'No'
                ) {
                    this.partiesList.push({
                        Name: data?.snsAppName,
                        Id: 0,
                    });
                    this.financingInfoForm.patchValue({ party: '0' });
                    this.companyName = data?.snsBizName;
                    this.showVerifyCompanyRegNoBtn = false;
                    this.showBRNVerified = true;
                    this.disableCompanyRegNo = true;
                    this.financingInfoForm.get('companyRegNo').disable();

                    this.showVerifyIdentificationNoBtn = false;
                    this.showVerifyIdentificationNo = true;
                    this.showKYCVerified = true;
                    this.successVerifyIdentification = true;
                    this.disableIdentificationNo = true;
                    this.financingInfoForm.get('identificationNo').disable();
                } else if (
                    data.constitutionTypeStdCode == 'PN' &&
                    this.yesNoOptionType == 'No'
                ) {
                    this.ownersList.push({
                        Name: data.snsAppName,
                        Id: 0,
                    });
                    this.financingInfoForm.patchValue({ owner: '0' });
                    this.companyName = data?.snsBizName;
                    this.showVerifyCompanyRegNoBtn = false;
                    this.showBRNVerified = true;
                    this.disableCompanyRegNo = true;
                    this.financingInfoForm.get('companyRegNo').disable();

                    this.showVerifyIdentificationNoBtn = false;
                    this.showVerifyIdentificationNo = true;
                    this.showKYCVerified = true;
                    this.successVerifyIdentification = true;
                    this.disableIdentificationNo = true;
                    this.financingInfoForm.get('identificationNo').disable();
                }
                regNo = data.regNo;
                if (data?.oldBizRegNo) {
                    if (data?.oldBizRegNo !== data?.regNo) {
                        this.showOldBizRegNo = true;
                    }
                }
                identificationNo = data?.directorNric;
            }
        } else {
            regNo = data?.regNo;
        }

        if (data?.constitutionTypeStdCode == 'IND') {
            this.financingInfoForm.patchValue({
                individualNRICName: data?.snsAppName,
            });
        }

        let address = data?.address;
        this.stateName = address?.state;
        if (this.stateName) {
            this.getCitiesList(this.stateName);
            this.showCities = true;
            this.cityName = address?.city;
        }
        this.financingInfoForm.patchValue({
            financingPurpose: data?.purposeOfFinancing || '',
            prefferedAmount: formatPrefferedAmount
                ? 'RM' +
                  formatPrefferedAmount
                      ?.replace(/\D/g, '')
                      .replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                : '',
            tenure: data?.tenureYears || '',
            businessType:
                data?.constitutionTypeStdCode ||
                this.userData?.details?.testconsttype ||
                '',
            sabahSarwak: this.yesNoOptionType,
            companyRegNo: regNo,
            oldCompanyRegNo: data?.oldBizRegNo,
            identificationNo: identificationNo,
            companyNRICName:
                data?.snsBizName || this.userData?.details?.bizname || '',
            individualNRICNo: data?.directorNric,
            mgtNRICName:
                data?.snsAppName || this.userData?.details?.fullname || '',
            businessAddress1: address?.bussLine1 || '',
            businessAddress2: address?.bussLine2,
            state: this.stateName,
            city: this.cityName,
            postCode: address?.postCode,
            bumiputeraStatus: this.bumiputeraType,
            age: data?.keyMgtAge || '',
        });

        if (this.financingInfoForm.value.mgtNRICName !== '') {
            this.disableApplicantName = true;
            this.financingInfoForm.get('mgtNRICName').disable();
        } else {
            this.disableApplicantName = false;
            this.financingInfoForm.get('mgtNRICName').enable();
        }
    }

    // showDirectorNRIC(directorNric: string) {
    //     if (this.checkStatus == 'NRIC') {
    //         return directorNric;
    //     } else {
    //         return '';
    //     }
    // }

    async getCaseDetails(caseId: string) {
        const value = await this.applicationService
            .getCaseDetails(caseId)
            .toPromise();
        return value[0];
    }

    getStatesList() {
        this.applicationService.getStates().subscribe({
            next: (res) => {
                let statesList = res.states;
                statesList.map((element) => {
                    this.stateOptions.push({
                        value: element,
                        label: element,
                    });
                });
            },
            error: (err) => {
                this.toastr.error(err.error.message);
            },
        });
    }

    getBRNLabelText = () => {
        return this.showOldBizRegNo
            ? 'NEW_APPLICATION.NEW_COMPANY_REG_NUMBER'
            : 'NEW_APPLICATION.COMPANY_REG_NUMBER';
    };

    getErrors(input: any, inputName?: any): Array<any> {
        let errors = [];
        this.updateTranslation();
        if (this.submitted) {
            if (
                input == 'companyRegNo' &&
                (this.f[input].errors?.required ||
                    this.f[input].errors?.minlength ||
                    this.f[input].errors?.maxlength)
            ) {
                if (this.financingInfoForm.value.sabahSarwak === 'No') {
                    errors.push(this.companyRegNoError);
                } else if (this.financingInfoForm.value.sabahSarwak === 'Yes') {
                    errors.push(this.companyRegNoError2);
                }
            } else if (
                input == 'identificationNo' &&
                (this.f[input].errors?.required ||
                    this.f[input].errors?.minlength ||
                    this.f[input].errors?.maxlength)
            ) {
                errors.push(this.identificationNoError);
            } else if (
                input == 'individualNRICNo' &&
                (this.f[input].errors?.required ||
                    this.f[input].errors?.minlength ||
                    this.f[input].errors?.maxlength)
            ) {
                errors.push(this.individualIdentificationNoError);
            } else if (
                input == 'individualNRICName' &&
                (this.f[input].errors?.required ||
                    this.f[input].errors?.minlength ||
                    this.f[input].errors?.maxlength)
            ) {
                errors.push(this.individualNRICNameError);
            } else if (
                input == 'mgtNRICName' &&
                (this.f[input].errors?.required ||
                    this.f[input].errors?.maxlength)
            ) {
                errors.push(this.mgtNRICNameError);
            } else if (
                input == 'companyNRICName' &&
                (this.f[input].errors?.required ||
                    this.f[input].errors?.maxlength)
            ) {
                errors.push(this.companyNRICNameError);
            } else if (input == 'age' && this.f[input].errors?.required) {
                errors.push(this.ageRequiredError);
            } else if (
                input == 'businessAddress1' &&
                this.f[input].errors?.required
            ) {
                errors.push(this.businessAddress1Error);
            } else if (
                input == 'postCode' &&
                (this.f[input].errors?.required ||
                    this.f[input].errors?.minlength)
            ) {
                errors.push(this.postCodeError);
            } else if (
                input !== 'companyRegNo' &&
                input !== 'identificationNo' &&
                input !== 'individualNRICName' &&
                input !== 'individualNRICNo' &&
                input !== 'age'
            ) {
                if (this.f[input].errors?.required) {
                    errors.push(inputName);
                }
            }
            if (input == 'age' && this.f[input].errors?.min) {
                errors.push(this.minAgeError);
            }
            if (
                input == 'prefferedAmount' &&
                (this.f[input].errors?.min || this.f[input].errors?.max)
            ) {
                errors.push(this.minMaxPrefAmountError);
            }
            if (
                input == 'tenure' &&
                (this.f[input].errors?.min || this.f[input].errors?.max)
            ) {
                errors.push(this.tenureError);
            }
        }
        return errors;
    }

    closeAllDropdowns(state: number) {
        for (let i = 0; i < 8; i++) {
            if (i != state) {
                this.toggleDropDown[i] = false;
            } else {
                this.toggleDropDown[i] = true;
            }
        }
        event.stopPropagation();
    }
    // toggleSelect(num: number) {
    //     for (let i = 0; i < 3; i++) {
    //         if (i == num) {
    //             this.toggleDropDown[i] = true;
    //         } else {
    //             this.toggleDropDown[i] = false;
    //         }
    //     }
    //     event.stopPropagation();
    // }
    handlePurposeOfFinancingOptions(value: any) {
        this.financingPurpose = value;
        this.financingInfoForm.patchValue({
            financingPurpose: this.financingPurpose,
        });
    }

    handleTenureOption(value: any) {
        this.tenure = value;
        this.financingInfoForm.patchValue({
            tenure: this.tenure,
        });
    }

    handleBusinessTypeOptions(value: any) {
        this.isRefresh = false;
        this.showBRNVerified = false;
        this.businessType = value;
        this.financingInfoForm.patchValue({
            businessType: this.businessType,
        });
        this.financingInfoForm.get('companyRegNo').reset();
        this.financingInfoForm.get('companyRegNo').enable();
        this.financingInfoForm.get('identificationNo').reset();
        this.financingInfoForm.get('identificationNo').enable();
        // this.financingInfoForm.get('companyNRICName').reset();
        this.financingInfoForm.get('individualNRICNo').reset();
        // this.financingInfoForm.get('mgtNRICName').reset();

        this.disableCompanyRegNo = false;
        this.disableIdentificationNo = false;
        this.showCompanyRegNo = false;
        this.showVerifyCompanyRegNoBtn = false;
        this.companyRegNoVerifyLoading = false;
        this.identificationNoLoading = false;
        this.showKYCVerified = false;
        this.showDirectorList = false;
        this.showOwnersList = false;
        this.showPartiesList = false;
        this.selectedDirectorIndex = null;
        this.selectedOwnerIndex = null;
        this.selectedPartyIndex = null;
        this.financingInfoForm.get('sabahSarwak').reset();
        this.yesNoOptionType = '';
        this.companyName = '';
        this.directorsList = [];
        this.ownersList = [];
        this.partiesList = [];
        this.showIndividualNRICName = false;
        this.showMgtNRICName = false;
        this.showIndividualNRICNo = false;
        this.showCompanyNRICName = false;

        this.showOldBizRegNo = false; // hiding Old Biz Number field
    }

    setSelectSabahSarwak(element: string) {
        let sabahSarwakValue = element;
        this.showSabahSarwakFields(sabahSarwakValue);
    }

    onSelectSabahSarwak(value: any) {
        this.isRefresh = false;
        let sabahSarwakValue = value;
        this.yesNoOptionType = value;
        this.financingInfoForm.patchValue({
            sabahSarwak: this.yesNoOptionType,
        });

        this.financingInfoForm.get('companyRegNo').reset();
        this.financingInfoForm.get('companyRegNo').enable();
        this.financingInfoForm.get('identificationNo').reset();
        this.financingInfoForm.get('identificationNo').enable();
        // this.financingInfoForm.get('companyNRICName').reset();
        this.financingInfoForm.get('individualNRICNo').reset();
        // this.financingInfoForm.get('mgtNRICName').reset();

        this.disableCompanyRegNo = false;
        this.disableIdentificationNo = false;
        this.showBRNVerified = false;
        this.showKYCVerified = false;
        this.showVerifyIdentificationNoBtn = true;
        this.companyRegNoVerifyLoading = false;
        this.identificationNoLoading = false;
        this.companyName = '';
        this.directorsList = [];
        this.ownersList = [];
        this.partiesList = [];
        this.selectedDirectorIndex = null;
        this.showOldBizRegNo = false; // hiding Old Biz Number field

        this.showSabahSarwakFields(sabahSarwakValue);
    }

    showSabahSarwakFields(sabahSarwakValue) {
        //show fields based on sabah sarawak values
        if (sabahSarwakValue === 'Yes' || sabahSarwakValue === 'Ya') {
            this.showOwnersList = false;
            this.showPartiesList = false;
            // check business type
            if (this.businessType == 'LLP') {
                this.showCompanyRegNo = true;
                this.showVerifyCompanyRegNoBtn = false;
                this.showDirectorList = false;
                this.showIndividualNRICName = false;
                this.showMgtNRICName = true;
                this.showIndividualNRICNo = true;
                this.showCompanyNRICName = true;
            } else if (this.businessType == 'PROFBODIES') {
                this.showCompanyRegNo = true;
                this.showVerifyCompanyRegNoBtn = false;
                this.showDirectorList = false;
                this.showIndividualNRICName = false;
                this.showMgtNRICName = true;
                this.showIndividualNRICNo = true;
                this.showCompanyNRICName = true;
            } else if (this.businessType == 'PN') {
                this.showCompanyRegNo = true;
                this.showVerifyCompanyRegNoBtn = false;
                this.showDirectorList = false;
                this.showIndividualNRICName = false;
                this.showMgtNRICName = true;
                this.showIndividualNRICNo = true;
                this.showCompanyNRICName = true;
            } else if (this.businessType == 'SP') {
                this.showCompanyRegNo = true;
                this.showVerifyCompanyRegNoBtn = false;
                this.showDirectorList = false;
                this.showIndividualNRICName = false;
                this.showMgtNRICName = true;
                this.showIndividualNRICNo = true;
                this.showCompanyNRICName = true;
            } else if (this.businessType == 'SDNBHD') {
                this.showCompanyRegNo = true;
                this.showVerifyCompanyRegNoBtn = true;
                this.showDirectorList = true;
                this.showIndividualNRICName = false;
                this.showMgtNRICName = false;
                this.showIndividualNRICNo = false;
                this.showCompanyNRICName = false;
            } else if (this.businessType == 'IND') {
                this.showCompanyRegNo = false;
                this.showVerifyCompanyRegNoBtn = false;
                this.showDirectorList = false;
                this.showIndividualNRICName = true;
                this.showMgtNRICName = false;
                this.showIndividualNRICNo = true;
                this.showCompanyNRICName = true;
            }
        } else if (sabahSarwakValue === 'No' || sabahSarwakValue === 'Tidak') {
            //check business type
            if (this.businessType == 'LLP') {
                this.showCompanyRegNo = true;
                this.showVerifyCompanyRegNoBtn = false;
                this.showDirectorList = false;
                this.showOwnersList = false;
                this.showPartiesList = false;
                this.showIndividualNRICName = false;
                this.showMgtNRICName = true;
                this.showIndividualNRICNo = true;
                this.showCompanyNRICName = true;
            } else if (this.businessType == 'PROFBODIES') {
                this.showCompanyRegNo = true;
                this.showVerifyCompanyRegNoBtn = false;
                this.showDirectorList = false;
                this.showOwnersList = false;
                this.showPartiesList = false;
                this.showIndividualNRICName = false;
                this.showMgtNRICName = true;
                this.showIndividualNRICNo = true;
                this.showCompanyNRICName = true;
            } else if (this.businessType == 'PN') {
                this.showCompanyRegNo = true;
                this.showVerifyCompanyRegNoBtn = true;
                this.showDirectorList = false;
                this.showOwnersList = true;
                this.showPartiesList = false;
                this.showIndividualNRICName = false;
                this.showMgtNRICName = false;
                this.showIndividualNRICNo = false;
                this.showCompanyNRICName = false;
            } else if (this.businessType == 'SP') {
                this.showCompanyRegNo = true;
                this.showVerifyCompanyRegNoBtn = true;
                this.showDirectorList = false;
                this.showOwnersList = false;
                this.showPartiesList = true;
                this.showIndividualNRICName = false;
                this.showMgtNRICName = false;
                this.showIndividualNRICNo = false;
                this.showCompanyNRICName = false;
            } else if (this.businessType == 'SDNBHD') {
                this.showCompanyRegNo = true;
                this.showVerifyCompanyRegNoBtn = true;
                this.showDirectorList = true;
                this.showOwnersList = false;
                this.showPartiesList = false;
                this.showIndividualNRICName = false;
                this.showMgtNRICName = false;
                this.showIndividualNRICNo = false;
                this.showCompanyNRICName = false;
            } else if (this.businessType == 'IND') {
                this.showCompanyRegNo = false;
                this.showVerifyCompanyRegNoBtn = false;
                this.showDirectorList = false;
                this.showOwnersList = false;
                this.showPartiesList = false;
                this.showIndividualNRICName = true;
                this.showMgtNRICName = false;
                this.showIndividualNRICNo = true;
                this.showCompanyNRICName = true;
            }
        }

        if (this.showCompanyRegNo === true) {
            this.financingInfoForm
                .get('companyRegNo')
                .setValidators([
                    Validators.required,
                    Validators.minLength(1),
                    Validators.maxLength(20),
                ]);
            this.financingInfoForm.get('companyRegNo').updateValueAndValidity();
        } else {
            this.financingInfoForm.get('companyRegNo').clearValidators();
            this.financingInfoForm.get('companyRegNo').updateValueAndValidity();
        }

        if (
            this.showDirectorList === true ||
            this.showPartiesList === true ||
            this.showOwnersList === true
        ) {
            this.financingInfoForm
                .get('identificationNo')
                .setValidators([
                    Validators.required,
                    Validators.minLength(12),
                    Validators.maxLength(12),
                ]);
            this.financingInfoForm
                .get('identificationNo')
                .updateValueAndValidity();
        } else {
            this.financingInfoForm.get('identificationNo').clearValidators();
            this.financingInfoForm
                .get('identificationNo')
                .updateValueAndValidity();
        }

        if (this.showIndividualNRICName === true) {
            this.financingInfoForm
                .get('individualNRICName')
                .setValidators([
                    Validators.required,
                    Validators.minLength(3),
                    Validators.maxLength(50),
                ]);
            this.financingInfoForm
                .get('individualNRICName')
                .updateValueAndValidity();
        } else {
            this.financingInfoForm.get('individualNRICName').clearValidators();
            this.financingInfoForm
                .get('individualNRICName')
                .updateValueAndValidity();
        }

        if (this.showIndividualNRICNo === true) {
            this.financingInfoForm
                .get('individualNRICNo')
                .setValidators([
                    Validators.required,
                    Validators.minLength(12),
                    Validators.maxLength(12),
                ]);
            this.financingInfoForm
                .get('individualNRICNo')
                .updateValueAndValidity();
        } else {
            this.financingInfoForm.get('individualNRICNo').clearValidators();
            this.financingInfoForm
                .get('individualNRICNo')
                .updateValueAndValidity();
        }

        if (this.showMgtNRICName === true) {
            this.financingInfoForm
                .get('mgtNRICName')
                .setValidators([Validators.required, Validators.maxLength(30)]);
            this.financingInfoForm.get('mgtNRICName').updateValueAndValidity();
        } else {
            this.financingInfoForm.get('mgtNRICName').clearValidators();
            this.financingInfoForm.get('mgtNRICName').updateValueAndValidity();
        }

        if (this.showCompanyNRICName === true) {
            this.financingInfoForm
                .get('companyNRICName')
                .setValidators([Validators.required, Validators.maxLength(50)]);
            this.financingInfoForm
                .get('companyNRICName')
                .updateValueAndValidity();
        } else {
            this.financingInfoForm.get('companyNRICName').clearValidators();
            this.financingInfoForm
                .get('companyNRICName')
                .updateValueAndValidity();
        }
    }

    handleDirectorChange(index: number) {
        this.selectedDirectorIndex = index;
        this.selectedDirector = this.directorsList[index];
        this.showVerifyIdentificationNo = true;
        this.disableVerifyIdentificationNo = false;
    }

    handleOwnerChange(index: number) {
        this.selectedOwnerIndex = index;
        this.selectedOwner = this.ownersList[index];
        this.showVerifyIdentificationNo = true;
        this.disableVerifyIdentificationNo = false;
    }

    handlePartyChange(index: number) {
        this.selectedPartyIndex = index;
        this.selectedParty = this.partiesList[index];
        this.showVerifyIdentificationNo = true;
        this.disableVerifyIdentificationNo = false;
    }

    onRefreshClick() {
        this.isRefresh = true;
        this.isRefreshLoading = true;

        let isSabahSarwak;
        if (this.financingInfoForm.value.sabahSarwak === 'Yes') {
            isSabahSarwak = 'Y';
        } else if (this.financingInfoForm.value.sabahSarwak === 'No') {
            isSabahSarwak = 'N';
        }

        let refreshDetails = {
            caseId: this.caseId,
            regNo: this.financingInfoForm.getRawValue().companyRegNo,
            constitutionTypeStdCode: this.businessType,
            SabahSarawakRegistered: isSabahSarwak,
            reportType: 'BIR-P',
        };

        this.applicationService.refreshDetails(refreshDetails).subscribe({
            next: (response) => {
                let brnData = parseJSON(response.body);
                //To get directors list
                if (this.businessType == 'SDNBHD') {
                    this.financingInfoForm.get('director').reset();
                    let getDirectorsList =
                        brnData?.Report?.SMEProfile?.Directors?.Director;
                    this.directorsList = getDirectorsList;
                    let filteredDirectors = this.directorsList?.filter(
                        (item: any) => item.Designation == 'Director',
                    );
                    this.directorsList = filteredDirectors;
                }

                //To get list of owners for Partnership
                if (this.businessType == 'PN') {
                    this.financingInfoForm.get('owner').reset();
                    let getOwnersList =
                        brnData?.Report?.SMEProfile?.CurrentOwners
                            ?.CurrentOwner;
                    this.ownersList = getOwnersList;
                }

                //To get list of owners for SP
                if (this.businessType == 'SP') {
                    this.financingInfoForm.get('party').reset();
                    let getPartiesList =
                        brnData?.Report?.SMEProfile?.CurrentOwners
                            ?.CurrentOwner;
                    this.partiesList = getPartiesList;
                }
                this.companyName = brnData?.Report?.SMEProfile?.RegName;

                if (
                    this.businessType == 'SDNBHD' &&
                    this.directorsList.length > 0
                ) {
                    this.directorsList.forEach((element) => {
                        this.directorsDesignation.push(element.Designation);
                        this.directorsName.push(element.Name);
                    });
                    this.showDirectorList = true;
                } else {
                    this.showDirectorList = false;
                }

                if (this.businessType == 'PN' && this.ownersList.length > 0) {
                    this.ownersList.forEach((element) => {
                        this.ownersName.push(element.Name);
                    });
                    this.showOwnersList = true;
                } else {
                    this.showOwnersList = false;
                }

                if (this.businessType == 'SP' && this.partiesList.length > 0) {
                    this.partiesList.forEach((element) => {
                        this.ownersName.push(element.Name);
                    });
                    this.showPartiesList = true;
                } else {
                    this.showPartiesList = false;
                }

                this.isRefreshLoading = false;
                if (localStorage.getItem('language') == 'en') {
                    this.toastr.success('Customer details updated');
                } else {
                    this.toastr.success(
                        'Maklumat pelanggan berjaya dikemas kini',
                    );
                }
            },
            error: (error) => {
                this.isRefreshLoading = false;

                if (localStorage.getItem('language') == 'en') {
                    this.toastr.error('Fail to update customer details.');
                } else {
                    this.toastr.error('Maklumat gagal dikemas kini');
                }
            },
        });

        // user click refresh after IC verification or director/owner/party selection -> resets IC verification & director/owner/party selection
        if (
            (this.successVerifyIdentification =
                true ||
                this.selectedDirector ||
                this.selectedOwner ||
                this.selectedParty)
        ) {
            this.selectedDirector = null;
            this.selectedDirectorIndex = null;

            this.selectedOwner = null;
            this.selectedOwnerIndex = null;

            this.selectedParty = null;
            this.selectedPartyIndex = null;

            this.successVerifyIdentification = false;
            this.showVerifyIdentificationNoBtn = true;
            this.disableIdentificationNo = false;
            this.showKYCVerified = false;
            this.financingInfoForm.get('identificationNo').enable();
            this.financingInfoForm.get('identificationNo').reset();
        }
    }

    onVerifyCompanyRegNo(verifyClick: any, regNo, sabahSarwakOnResume) {
        this.successVerifyIdentification = false;
        if (verifyClick === true) {
            this.showBRNVerified = false;
            this.companyRegNoVerifyLoading = true;
            let isSabahSarwak;
            if (this.financingInfoForm.value.sabahSarwak === 'Yes') {
                isSabahSarwak = 'Y';
            } else if (this.financingInfoForm.value.sabahSarwak === 'No') {
                isSabahSarwak = 'N';
            } else {
                isSabahSarwak = sabahSarwakOnResume;
            }

            let brnDetails = {
                caseId: this.caseId,
                regNo: this.financingInfoForm.value.companyRegNo || regNo,
                constitutionTypeStdCode: this.businessType,
                SabahSarawakRegistered: isSabahSarwak,
            };
            this.applicationService.checkBRN(brnDetails).subscribe({
                next: (res) => {
                    let brnData = parseJSON(res.body);

                    //To get directors list
                    if (this.businessType == 'SDNBHD') {
                        this.financingInfoForm.get('director').reset();
                        let getDirectorsList =
                            brnData?.Report?.SMEProfile?.Directors?.Director;
                        this.directorsList = getDirectorsList;
                        let filteredDirectors = this.directorsList?.filter(
                            (item: any) => item.Designation == 'Director',
                        );
                        this.directorsList = filteredDirectors;
                    }

                    //To get list of owners for Partnership
                    if (this.businessType == 'PN') {
                        this.financingInfoForm.get('owner').reset();
                        let getOwnersList =
                            brnData?.Report?.SMEProfile?.CurrentOwners
                                ?.CurrentOwner;
                        this.ownersList = getOwnersList;
                    }

                    //To get list of owners for SP
                    if (this.businessType == 'SP') {
                        this.financingInfoForm.get('party').reset();
                        let getPartiesList =
                            brnData?.Report?.SMEProfile?.CurrentOwners
                                ?.CurrentOwner;
                        this.partiesList = getPartiesList;
                    }
                    this.companyName = brnData?.Report?.SMEProfile?.RegName;

                    if (
                        this.businessType == 'SDNBHD' &&
                        this.directorsList.length > 0
                    ) {
                        this.directorsList.forEach((element) => {
                            this.directorsDesignation.push(element.Designation);
                            this.directorsName.push(element.Name);
                        });
                        this.showDirectorList = true;
                    } else {
                        this.showDirectorList = false;
                    }

                    if (
                        this.businessType == 'PN' &&
                        this.ownersList.length > 0
                    ) {
                        this.ownersList.forEach((element) => {
                            this.ownersName.push(element.Name);
                        });
                        this.showOwnersList = true;
                    } else {
                        this.showOwnersList = false;
                    }

                    if (
                        this.businessType == 'SP' &&
                        this.partiesList.length > 0
                    ) {
                        this.partiesList.forEach((element) => {
                            this.ownersName.push(element.Name);
                        });
                        this.showPartiesList = true;
                    } else {
                        this.showPartiesList = false;
                    }

                    this.showVerifyCompanyRegNoBtn = false;
                    this.companyRegNoVerifyLoading = false;
                    this.showBRNVerified = true;
                    this.disableCompanyRegNo = true;
                    this.financingInfoForm.get('companyRegNo').disable();
                },
                error: (err) => {
                    let parseErr = parseJSON(err);
                    let errorObj = parseErr?.error;
                    let errMessage = errorObj.message;
                    let errorCode = parseErr?.status;

                    this.showVerifyCompanyRegNoBtn = true;
                    this.showBRNVerified = false;

                    if (errorCode === 400) {
                        this.companyRegNoVerifyLoading = false;
                        if (localStorage.getItem('language') == 'en') {
                            this.toastr.error(
                                'Business Registration No. or Business Type is incorrect',
                            );
                        } else {
                            this.toastr.error(
                                'Nombor Pendaftaran atau Jenis Perniagaan tidak tepat',
                            );
                        }
                    } else if (errorCode === 504) {
                        this.companyRegNoVerifyLoading = true;
                        setTimeout(() => {
                            this.companyRegNoVerifyLoading = false;
                        }, 30000);
                    } else {
                        this.companyRegNoVerifyLoading = false;
                        this.toastr.error(errMessage);
                    }
                },
            });
        }
    }

    onVerfiyIdentificationNo(
        verifyClick: any,
        showDirectorList?,
        showOwnersList?,
        showPartiesList?,
    ) {
        this.updateTranslation();
        if (showDirectorList === true && this.selectedDirectorIndex == null) {
            let message = this.selectDirectorError;
            this.toastr.error(message);
        } else if (showOwnersList === true && this.selectedOwnerIndex == null) {
            let message = this.selectOwnerError;
            this.toastr.error(message);
        } else if (
            showPartiesList === true &&
            this.selectedPartyIndex == null
        ) {
            let message = this.selectPartyError;
            this.toastr.error(message);
        } else {
            this.showKYCVerified = false;
            this.identificationNoLoading = true;

            let kycDetails = {
                caseId: this.caseId,
                regNo: this.financingInfoForm.getRawValue().companyRegNo,
                directorNric: this.financingInfoForm.value.identificationNo,
            };

            if (this.businessType == 'SDNBHD') {
                kycDetails['directorId'] = this.selectedDirector?.Id;
            } else if (this.businessType == 'PN') {
                kycDetails['directorId'] = this.selectedOwner?.Id;
            } else if (this.businessType == 'SP') {
                kycDetails['directorId'] = this.selectedParty?.Id;
            }

            this.applicationService.checkKYC(kycDetails).subscribe({
                next: (res) => {
                    this.successVerifyIdentification = true;
                    this.identificationNoLoading = false;
                    this.showKYCVerified = true;
                    this.showVerifyIdentificationNoBtn = false;
                    this.financingInfoForm.get('identificationNo').disable();
                    this.disableIdentificationNo = true;

                    let verifiedName = res?.detail?.directorName;
                    let verifiedId = res?.detail?.id;

                    // to remove directors for SDNBHD
                    if (
                        this.businessType == 'SDNBHD' &&
                        this.directorsList.length > 0
                    ) {
                        this.directorsList = this.directorsList.filter(
                            (e) => e.Id == verifiedId,
                        );
                        this.directorsList.forEach((element) => {
                            return (element.Name = verifiedName);
                        });
                    }

                    // to remove directors for SP
                    if (
                        this.businessType == 'SP' &&
                        this.partiesList.length > 0
                    ) {
                        this.partiesList = this.partiesList.filter(
                            (e) => e.Id == verifiedId,
                        );
                        this.partiesList.forEach((element) => {
                            return (element.Name = verifiedName);
                        });
                    }

                    // to remove parties for PN
                    if (
                        this.businessType == 'PN' &&
                        this.ownersList.length > 0
                    ) {
                        this.ownersList = this.ownersList.filter(
                            (e) => e.Id == verifiedId,
                        );
                        this.ownersList.forEach((element) => {
                            return (element.Name = verifiedName);
                        });
                    }
                },
                error: (err) => {
                    this.identificationNoLoading = false;
                    this.showKYCVerified = false;
                    this.showVerifyIdentificationNoBtn = true;
                    let errorResponse: any = parseJSON(err);
                    let errorObj = errorResponse?.error;
                    let errMessage = errorObj.message;
                    if (localStorage.getItem('language') == 'bm') {
                        this.toastr.error(
                            'No. kad pengenalan tidak sah. Sila cuba lagi.',
                        );
                    } else {
                        this.toastr.error(
                            'The NRIC no. is not valid. Please try again.',
                        );
                    }
                },
            });
        }
    }

    onSelectState(value: any) {
        this.stateName = value;
        this.financingInfoForm.patchValue({
            state: this.stateName,
        });
        this.getCitiesList(value);
        this.showCities = true;
        this.cityName = null;
        this.financingInfoForm.get('city').reset();
        this.cityOptions = [];
    }

    getCitiesList(stateName) {
        this.applicationService.getCities(stateName).subscribe({
            next: (res) => {
                let citiesList = res.cities;
                citiesList.map((element) => {
                    this.cityOptions.push({
                        value: element,
                        label: element,
                    });
                });
            },
            error: (err) => {
                this.toastr.error(err.error.message);
            },
        });
    }

    onSelectCity(value: any) {
        this.cityName = value;
        this.financingInfoForm.patchValue({
            city: this.cityName,
        });
    }

    onSelectBumiputera(value: any) {
        this.bumiputeraValue = value;
        this.bumiputeraType = value;
        this.financingInfoForm.patchValue({
            bumiputeraStatus: this.bumiputeraType,
        });
    }

    async getCaseStatus(regNo, directorNric, constitutionTypeStdCode) {
        this.disableSubmitBtn = true;
        return await lastValueFrom(
            this._dashboardService.caseStatus().pipe(
                map((response) => {
                    if (response) {
                        response.forEach((caseData) => {
                            this.duplicateProdCode.push(caseData.cCaseProdSel);
                            if (
                                this.duplicateProdCode.includes(this.prodCode)
                            ) {
                                let blockedStatus = this.statusOptions.includes(
                                    caseData.cCaseStatus,
                                );
                                if (blockedStatus) {
                                    if (
                                        this.prodCode === caseData.cCaseProdSel
                                    ) {
                                        if (
                                            directorNric ==
                                                caseData.cCaseNric &&
                                            constitutionTypeStdCode ===
                                                caseData.cCaseBizConst
                                        ) {
                                            if (regNo) {
                                                if (
                                                    regNo ==
                                                        caseData.cCaseBizRegNo ||
                                                    regNo ==
                                                        caseData.cCaseOldBizRegNo
                                                ) {
                                                    this.isDuplicateApp = true;
                                                    this.duplicateAppNo =
                                                        caseData.cCaseAppNo;
                                                }
                                            } else if (!regNo) {
                                                this.isDuplicateApp = true;
                                                this.duplicateAppNo =
                                                    caseData.cCaseAppNo;
                                            }
                                            return;
                                        }
                                    }
                                }
                            }
                        });
                        this.getCaseStatusResponse = true;
                        this.disableSubmitBtn = true;
                    } else {
                        this.getCaseStatusResponse = false;
                        this.disableSubmitBtn = false;
                        throw new Error('Something went wrong!');
                    }
                }),
            ),
        );
    }

    duplicateAppMsg(appNo) {
        let data = {
            productFlowData: {
                prodCode: this.prodCode,
                type: 'duplicate_app',
                appNo: appNo,
            },
        };
        let dRef = this.dialog.open(ConfirmInfoComponent, {
            backdropClass: 'confirminfo-dialog-background',
            hasBackdrop: true,
            scrollStrategy: new NoopScrollStrategy(),
            panelClass: 'confirminfo-custom-dialog',
            data,
            disableClose: true,
        });
        dRef.afterClosed().subscribe((res) => {
            this.disableSubmitBtn = false;
            if (res?.data == 'go_to_applications') {
                this.router.navigate(['/dashboard']);
            }
        });
    }

    async onFormSubmit(event, data) {
        this.regBrnNum = data.companyRegNo;
        this.closeDropdown();
        this.updateTranslation();

        let isSabahSarwak;
        if (this.financingInfoForm.value.sabahSarwak === 'Yes') {
            isSabahSarwak = 'Y';
        } else if (this.financingInfoForm.value.sabahSarwak === 'No') {
            isSabahSarwak = 'N';
        }

        if (this.financingInfoForm.value.bumiputeraStatus === 'Yes') {
            this.bumiputeraValue = 'Y';
        } else if (this.financingInfoForm.value.bumiputeraStatus === 'No') {
            this.bumiputeraValue = 'N';
        }

        let prefferedAmountValue;
        let convertedPrefferedAmount;

        if (this.financingInfoForm.value.prefferedAmount) {
            this.financingInfoForm.value.prefferedAmount =
                this.financingInfoForm.value.prefferedAmount
                    .replace('RM', '')
                    .replace(/,/g, '');
            if (isEmpty(this.financingInfoForm.value.prefferedAmount)) {
                //If user inputs value and then removes value
                this.financingInfoForm.controls['prefferedAmount'].setValue(
                    null,
                );
                convertedPrefferedAmount = null;
            } else {
                prefferedAmountValue = parseInt(
                    this.financingInfoForm.value.prefferedAmount,
                );
                convertedPrefferedAmount = prefferedAmountValue.toString();
            }
        } else {
            convertedPrefferedAmount = null;
        }

        let financingInfoDetails = {
            caseId: this.caseId,
            purposeOfFinancing: data.financingPurpose,
            financingAmount: convertedPrefferedAmount,
            tenureYears: !this.isDGP ? data.tenure : null,
            tenureUnit: this.isDGP
                ? `${this.prodValidation?.tenure?.min}-${this.prodValidation?.tenure?.max}-${this.prodValidation?.tenure?.unit}`
                : this.tenureUnit,
            SabahSarawakRegistered: isSabahSarwak,
            bumiputeraStatus: this.bumiputeraValue,
            constitutionTypeStdCode: data.businessType,
            keyMgtAge: data.age,
            address: {
                bussLine1: data.businessAddress1,
                bussLine2: data.businessAddress2,
                state: data.state,
                city: data.city,
                postCode: data.postCode,
            },
        };
        if (data.businessType !== 'IND') {
            financingInfoDetails['regNo'] =
                this.financingInfoForm.getRawValue().companyRegNo;
        }

        if (
            data.businessType == 'SDNBHD' ||
            (data.businessType == 'PN' && data.sabahSarwak == 'No') ||
            (data.businessType == 'SP' && data.sabahSarwak == 'No')
        ) {
            financingInfoDetails['directorNric'] =
                this.financingInfoForm.getRawValue().identificationNo;
        } else if (this.showIndividualNRICNo === true) {
            //identification no (SP and PN with sabahsarwak YES,LLP,PROFBODIES,IND)
            financingInfoDetails['directorNric'] = data.individualNRICNo;
        } else {
            //send directorNric as mandatory to send in case of draft if its value is not filled then send null
            financingInfoDetails['directorNric'] = null;
        }

        if (this.showIndividualNRICName === true) {
            //snsAppName means management person name (IND)
            financingInfoDetails['snsAppName'] = data.individualNRICName;
        } else if (this.showMgtNRICName === true) {
            //snsAppName means management person name (SP and PN with sabahsarwak YES,LLP,PROFBODIES)
            //name now changed to applicant name in UI
            financingInfoDetails['snsAppName'] =
                data.mgtNRICName || this.userData?.details?.fullname;
        }

        if (this.showCompanyNRICName === true) {
            //snsBizName means company name related to that identification no (SP and PN with sabahsarwak YES,LLP,PROFBODIES)
            financingInfoDetails['snsBizName'] = data.companyNRICName
                ? data.companyNRICName.toUpperCase()
                : null;
        }

        localStorage.setItem('typeOfBusiness', data.businessType);

        if (event.submitter.name == 'draft') {
            this.submitted = false;
            this.isDraft = true;
            financingInfoDetails['saveDraft'] = this.isDraft;
            if (!isSabahSarwak) {
                financingInfoDetails['SabahSarawakRegistered'] = '';
            }
            if (!this.bumiputeraValue) {
                financingInfoDetails['bumiputeraStatus'] = '';
            }
            this.onSaveFinancingInfo(financingInfoDetails);
        }
        if (event.submitter.name == 'next') {
            this.submitted = true;
            if (this.financingInfoForm.invalid) {
                scrollToFirstInvalidControl(this.formContainer.nativeElement);
            }
            if (
                prefferedAmountValue < 1000 ||
                prefferedAmountValue > 20000000
            ) {
                this.financingInfoForm.controls['prefferedAmount'].setErrors({
                    min: 1000,
                    max: 20000000,
                });
                scrollToFirstInvalidControl(this.formContainer.nativeElement);
            }

            this.isDraft = false;
            financingInfoDetails['saveDraft'] = this.isDraft;

            if (
                this.showCompanyRegNo == true &&
                this.showVerifyCompanyRegNoBtn == true &&
                this.showBRNVerified == false
            ) {
                this.toastr.error(this.verifyCompanyRegNoError);
            }

            if (this.financingInfoForm.valid) {
                //check for duplicate application
                this.isDuplicateApp = false;
                if (this.prodCode) {
                    await this.getCaseStatus(
                        financingInfoDetails['regNo'],
                        financingInfoDetails['directorNric'],
                        financingInfoDetails['constitutionTypeStdCode'],
                    );
                    if (this.isDuplicateApp && this.getCaseStatusResponse) {
                        this.duplicateAppMsg(this.duplicateAppNo);
                        return;
                    }
                }

                if (
                    this.showCompanyRegNo == true &&
                    this.showVerifyCompanyRegNoBtn == false &&
                    this.showBRNVerified == true
                ) {
                    if (
                        data.businessType == 'SDNBHD' ||
                        data.businessType == 'PN' ||
                        data.businessType == 'SP'
                    ) {
                        if (
                            this.showVerifyIdentificationNo == true &&
                            this.showKYCVerified == true
                        ) {
                            this.onSubmitFinancingInfo(financingInfoDetails);
                        } else {
                            this.toastr.error(this.verifyIdentificationNoError);
                        }
                    } else {
                        this.onSubmitFinancingInfo(financingInfoDetails);
                    }
                } else if (
                    this.showCompanyRegNo == true &&
                    this.showVerifyCompanyRegNoBtn == true &&
                    this.showBRNVerified == false
                ) {
                    this.toastr.error(this.verifyCompanyRegNoError);
                } else if (
                    this.showCompanyRegNo == true &&
                    this.showVerifyCompanyRegNoBtn == false &&
                    this.showBRNVerified == false
                ) {
                    this.onSubmitFinancingInfo(financingInfoDetails);
                } else if (this.showCompanyRegNo == false) {
                    this.onSubmitFinancingInfo(financingInfoDetails);
                }
            } else {
                return;
            }
        }
    }

    onSaveFinancingInfo(financingInfoDetails) {
        this.updateTranslation();
        this.translate
            .get('NEW_APPLICATION.FINANCING_INFO_SAVE_DRAFT_MSG')
            .subscribe((saveDraftMsg: any) => {
                this.applicationService
                    .submitFinancingInfo(financingInfoDetails)
                    .subscribe({
                        next: (res) => {
                            let formatResponse = parseJSON(res);
                            this.toastr.success(saveDraftMsg);
                        },
                        error: (err) => {
                            if (err?.error?.data?.financingAmount) {
                                if (localStorage.getItem('language') == 'bm') {
                                    this.toastr.error(
                                        this.financingAmountError,
                                    );
                                } else {
                                    this.toastr.error(
                                        err?.error?.data?.financingAmount,
                                    );
                                }
                            } else if (err?.error?.reason == 'Invalid step') {
                                this.invalidStep(err.error.message);
                            } else {
                                this.toastr.error(err.error.message);
                            }
                        },
                    });
            });
    }

    onSubmitFinancingInfo(financingInfoDetails) {
        this.disableSubmitBtn = true;
        this.applicationService
            .submitFinancingInfo(financingInfoDetails)
            .subscribe({
                next: (res) => {
                    this.router.navigate(['/business-info'], {
                        state: {
                            caseId: this.caseId,
                            fromResume: this.fromResume,
                            constitutionTypeStdCode:
                                res[0].constitutionTypeStdCode,
                            checkMatchingProduct: this.checkMatchingProduct,
                            productName: this.productName,
                            regNoo: this.regBrnNum,
                        },
                    });
                },
                error: (err) => {
                    this.disableSubmitBtn = false;
                    if (err?.error?.reason == 'Invalid step') {
                        this.invalidStep(err?.error?.message);
                    } else {
                        this.toastr.error(err.error.message);
                    }
                },
            });
    }

    invalidStep(message) {
        if (localStorage.getItem('language') == 'bm') {
            this.toastr.error(this.invalidStepError);
        } else {
            this.toastr.error(message);
        }
    }

    closeDropdown() {
        for (let i = 0; i < 8; i++) {
            this.toggleDropDown[i] = false;
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        if (
            changes['close']?.currentValue == true ||
            (changes['close']?.previousValue == true &&
                changes['close']?.currentValue == false)
        ) {
            this.closeDropdown();
        }
    }

    /* WILL COMMENT THIS FIRST AS FOR NOW */
    // @HostListener('window:scroll', [])
    // onWindowScroll() {
    //     const scrollableDiv = document.getElementById('scrollableDiv');
    //     const fixedDiv = document.getElementById('fixedDiv');
    //     const innerFixedDiv = document.getElementById('innerFixedDiv');
    //     window.addEventListener("scroll", () => {
    //         const scrollableDivRect = scrollableDiv.getBoundingClientRect();
    //         const scrollableBottom = scrollableDivRect.height;
    //         const initialPosition = fixedDiv.offsetTop;

    //         if (window.scrollY + initialPosition >= scrollableBottom) {
    //             // Once we reach the bottom of scrollableDiv, stop following
    //             innerFixedDiv.style.position = 'sticky';
    //         }
    //     });
    // }
}
